/**
 * Trial Lesson Progress Manager
 * Saves and restores user progress in the trial lesson when they're not authenticated
 */

const STORAGE_KEY = 'trialLessonProgress';

export const saveTrialProgress = (section, data) => {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    existing[section] = {
      ...data,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    console.log(`✅ Trial progress saved for ${section}:`, data);
  } catch (error) {
    console.error('Error saving trial progress:', error);
  }
};

export const getTrialProgress = (section) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    const sectionData = data[section];
    
    // Return null if data is older than 24 hours
    if (sectionData && sectionData.timestamp) {
      const hoursSinceLastSave = (Date.now() - sectionData.timestamp) / (1000 * 60 * 60);
      if (hoursSinceLastSave > 24) {
        console.log(`⏱️ Trial progress for ${section} expired (${hoursSinceLastSave.toFixed(1)}h old)`);
        return null;
      }
    }
    
    console.log(`📦 Trial progress loaded for ${section}:`, sectionData);
    return sectionData || null;
  } catch (error) {
    console.error('Error loading trial progress:', error);
    return null;
  }
};

export const clearTrialProgress = (section = null) => {
  try {
    if (section) {
      // Clear specific section
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      delete existing[section];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      console.log(`🗑️ Trial progress cleared for ${section}`);
    } else {
      // Clear all trial progress
      localStorage.removeItem(STORAGE_KEY);
      console.log('🗑️ All trial progress cleared');
    }
  } catch (error) {
    console.error('Error clearing trial progress:', error);
  }
};

export const hasTrialProgress = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return !!stored && Object.keys(JSON.parse(stored)).length > 0;
  } catch {
    return false;
  }
};

// Last accessed lesson tracking
const LAST_LESSON_KEY = 'lastAccessedLesson';

export const saveLastAccessedLesson = (lessonId, lessonData = {}) => {
  try {
    const data = {
      lessonId,
      timestamp: Date.now(),
      ...lessonData
    };
    localStorage.setItem(LAST_LESSON_KEY, JSON.stringify(data));
    console.log(`✅ Last accessed lesson saved: ${lessonId}`, data);
  } catch (error) {
    console.error('Error saving last accessed lesson:', error);
  }
};

export const getLastAccessedLesson = () => {
  try {
    const stored = localStorage.getItem(LAST_LESSON_KEY);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    
    // Return null if data is older than 7 days
    if (data.timestamp) {
      const daysSinceLastAccess = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
      if (daysSinceLastAccess > 7) {
        console.log(`⏱️ Last accessed lesson expired (${daysSinceLastAccess.toFixed(1)} days old)`);
        return null;
      }
    }
    
    console.log(`📦 Last accessed lesson loaded:`, data);
    return data;
  } catch (error) {
    console.error('Error loading last accessed lesson:', error);
    return null;
  }
};

export const clearLastAccessedLesson = () => {
  try {
    localStorage.removeItem(LAST_LESSON_KEY);
    console.log('🗑️ Last accessed lesson cleared');
  } catch (error) {
    console.error('Error clearing last accessed lesson:', error);
  }
};
